import {formatMoney} from '@shopify/theme-currency';
import PredictiveSearch from "@shopify/theme-predictive-search";
import { multilingual_url } from '../utils/multilingual-url';

const headerElement = document.querySelector('.header');

window.addEventListener('scroll', () => {
  const currentScrollPosition = window.pageYOffset;

  if(currentScrollPosition === 0) {
    headerElement.classList.remove('scrolled');
    headerElement.classList.add('back-to-top');
  }
  else {
    headerElement.classList.add('scrolled');
    headerElement.classList.remove('back-to-top');
  }
});

class CartLink extends HTMLElement {
  constructor() {
    super();

    this.cartLink = this.querySelector('[data-cart-link]');

    this.cartLink.addEventListener('click', (event) => {
      event.preventDefault();

      window.dispatchEvent(new Event('dynamic-cart-open'));
    })
  }
}

customElements.define('cart-link', CartLink);

class DynamicSearch extends HTMLElement {
  constructor() {
    super();

    this.searchInput = this.querySelector('[data-search-input]');
    this.headerSearchInput = this.querySelector('[data-header-search-input]');
    this.headerSearchDetails = this.querySelector('[data-header-search-details]');
    this.resultsContainer = this.querySelector('[data-results-container]');
    this.productResultsContainer = this.querySelector('[data-product-results-container]');
    this.pageArticleResultsContainer = this.querySelector('[data-page-article-results-container]');
    this.collectionResultsContainer = this.querySelector('[data-collection-results-container]');
    this.sustainableIconUrl = this.productResultsContainer.dataset.sustainableIcon;
    this.timer;

    this.predictiveSearch = new PredictiveSearch({
      search_path: multilingual_url('/search'),
      resources: {
        type: [
          PredictiveSearch.TYPES.PRODUCT,
          PredictiveSearch.TYPES.PAGE,
          PredictiveSearch.TYPES.ARTICLE,
          PredictiveSearch.TYPES.COLLECTION
        ],
        options: {
          unavailable_products: "hide",
          fields: [
            PredictiveSearch.FIELDS.TITLE,
            PredictiveSearch.FIELDS.BODY,
            PredictiveSearch.FIELDS.PRODUCT_TYPE,
            PredictiveSearch.FIELDS.VARIANTS_TITLE
          ]
        }
      }
    });

    this.searchInput.addEventListener('input', this.onSearchInputChange.bind(this));

    this.headerSearchInput.addEventListener('input', this.onSearchInputChange.bind(this));
    this.headerSearchInput.addEventListener('focusin', this.onHeaderSearchInputFocusIn.bind(this));
    this.headerSearchInput.addEventListener('focusout', this.onHeaderSearchInputFocusOut.bind(this));

    this.predictiveSearch.on("success", this.searchSuccess.bind(this));
    this.predictiveSearch.on("error", this.searchError.bind(this));
  }

  onHeaderSearchInputFocusIn() {
    this.headerSearchDetails.setAttribute('open', '');
  }

  onHeaderSearchInputFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)){
        this.headerSearchDetails.removeAttribute('open');
      }
    });
  }

  onSearchInputChange(event) {
    let searchInputValue = event.currentTarget.value;
    this.searchInput.value = searchInputValue;
    this.headerSearchInput.value = searchInputValue;

    clearTimeout(this.timer);

    if(searchInputValue != "")  {
      this.resultsContainer.classList.add('loading', 'loading-state');
    }
    else {
      this.resultsContainer.classList.remove('loading', 'loading-state');
      this.productResultsContainer.innerHTML = "";
      this.pageArticleResultsContainer.innerHTML = "";
      this.collectionResultsContainer.innerHTML = "";
    }

    this.timer = setTimeout(() => {
      this.predictiveSearch.searchPath = multilingual_url('/search');
      this.predictiveSearch.query(searchInputValue);
    }, 1000);
  }

  searchSuccess(json) {
    this.resultsContainer.classList.remove('loading', 'loading-state');

    let productCardsHtml = '';
    json.resources.results.products.forEach(product => {
      productCardsHtml += this.productCardTemplate(product);
    });

    this.productResultsContainer.innerHTML = productCardsHtml;

    let collectionHtml = '';
    json.resources.results.collections.forEach(collection => {
      collectionHtml += this.collectionTemplate(collection);
    });

    this.collectionResultsContainer.innerHTML = collectionHtml;

    let pageArticle = '';
    json.resources.results.pages.forEach(page => {
      pageArticle += this.pageTemplate(page);
    });

    json.resources.results.articles.forEach(article => {
      pageArticle += this.articleTemplate(article);
    });

    this.pageArticleResultsContainer.innerHTML = pageArticle;
  }

  searchError(error) {
    this.resultsContainer.classList.remove('loading', 'loading-state');

    this.productResultsContainer.innerHTML = "";
    this.pageArticleResultsContainer.innerHTML = "";
    this.collectionResultsContainer.innerHTML = "";

    window.toast.error({
      message: error.message,
      icon: false
    });
  }

  sustainableIcon(product) {
    if (product.tags.includes('sustainable')) {
      return `
      <img src="${this.sustainableIconUrl}" class="product-card__sustainable-icon" data-i18n='products.product.sustainable || { "attr": "alt" }' alt="{{ 'products.product.sustainable' | t }}">
      `;
    }

    return '';
  }

  productCardTemplate(product) {
    return `
      <div>
        <div class="product-card__image-wrapper">
          <a href="${product.url}">
            ${this.sustainableIcon(product)}
            <div class="responsive-image__wrapper ratio-rectangle">
              <img src="${product.featured_image.url}" alt="${product.featured_image.alt}" loading="lazy">
            </div>
          </a>
        </div>

        <div class="product-card__info">
          <h2 class="p product-card__title"><a href="${product.url}">${window.i18next.buildTextSpan(product.title)}</a></h2>
          <div class="price
            ${!product.available ? 'price--sold-out' : ''}
            ${product.compare_at_price_max > product.price ? 'price--on-sale' : ''}
            ${product.price_min != product.price_max && product.compare_at_price_min != product.compare_at_price_max ? 'price--no-compare' : ''}
          ">
            <dl><div class="price__regular">
                <dt>
                  <span class="visually-hidden visually-hidden--inline" data-i18n="products.product.price.regular_price"></span>
                </dt>
                <dd>
                  <span class="price-item price-item--regular" data-price="">
                    ${formatMoney(product.price * 100, theme.moneyFormat)}
                  </span>
                </dd>
              </div>
              <div class="price__sale">
                <dt class="price__compare">
                  <span class="visually-hidden visually-hidden--inline" data-i18n="products.product.price.regular_price"></span>
                </dt>
                <dd class="price__compare">
                  <s class="price-item price-item--regular" data-compare-at-price="">
                    ${formatMoney(product.compare_at_price_max * 100, theme.moneyFormat)}
                  </s>
                </dd>
                <dt>
                  <span class="visually-hidden visually-hidden--inline" data-i18n="products.product.price.sale_price">{{ 'products.product.price.sale_price' | t }}</span>
                </dt>
                <dd>
                  <span class="price-item price-item--sale" data-price="">
                    ${formatMoney(product.price * 100, theme.moneyFormat)}
                  </span>
                </dd>
              </div>

              <span class="badge price__badge-sold-out" aria-hidden="true" data-i18n="products.product.sold_out">
              </span>
            </dl>
          </div>

        </div>
      </div>
    `
  }

  collectionTemplate(collection) {
    return `<div class="header__collection-result"><a href="${collection.url}">${window.i18next.buildTextSpan(collection.title)}</a></div>`;
  }

  pageTemplate(page) {
    return `<div class="header__page-result"><a href="${page.url}">${window.i18next.buildTextSpan(page.title)}</a></div>`;
  }

  articleTemplate(article) {
    return `<div class="header__article-result"><a href="${article.url}">${window.i18next.buildTextSpan(article.title)}</a></div>`;
  }
}

customElements.define('dynamic-search', DynamicSearch);
